import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import {
  ChevronLeft, Eye, Camera, Activity, User, Users, MessageSquare,
  FileText, Plus, CheckCircle2, XCircle, Shield, Search,
  Upload, AlertTriangle, Flag, Fingerprint, Square, CheckSquare,
  ArrowRight, X, RefreshCw
} from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import CddTierCard from '../profile-safe/components/CddTierCard'
import PatronComplianceStrip from '../../components/patron/PatronComplianceStrip'
import ProfileAmlMatchReviewPanel from '../../components/aml/ProfileAmlMatchReviewPanel'
import { displayMembershipNumber, displayPlayerId } from '../../utils/patronIdentity'

const C = {
  pri: '#0a5169', priD: '#07384a', priL: '#1a7a9b', priM: '#e8f2f6',
  red: '#dc2626', redBg: '#fef2f2', redBd: '#fecaca',
  amber: '#d97706', amberBg: '#fffbeb', amberBd: '#fde68a',
  green: '#16a34a', greenBg: '#f0fdf4', greenBd: '#bbf7d0',
  purple: '#7c3aed', purpleBg: '#f5f3ff', purpleBd: '#ddd6fe',
  slate: '#64748b', slateL: '#94a3b8', slateBg: '#f8fafc',
}

const T = { small: 12, normal: 14 }

// S8-F1a — mapped to the migration 140 enum codes so the backend can
// reason about "why are members leaving the watchlist" without text-mining.
// Each label is shown to the user; the `code` is what goes on the PUT body.
const REMOVAL_REASONS = [
  { code: 'case_closed_satisfactory', label: 'Investigation / ECDD complete — no suspicious activity or risk satisfactorily explained' },
  { code: 'account_action_taken',     label: 'SMR filed or account action taken (suspend / restrict / close)' },
  { code: 'patron_requested_removal', label: 'Patron requested removal or no longer visits the venue' },
  { code: 'false_positive',           label: 'False positive — patron incorrectly flagged' },
  { code: 'duplicate_monitoring',     label: 'Duplicate entry — patron already monitored elsewhere' },
  { code: 'other',                    label: 'Other (specify in notes)' },
]

const fmt$ = n => '$' + (n ?? 0).toLocaleString()
const SC = {
  clear: { color: C.green, bg: C.greenBg, bd: C.greenBd, label: 'Clear' },
  review: { color: C.amber, bg: C.amberBg, bd: C.amberBd, label: 'Review' },
  match: { color: C.red, bg: C.redBg, bd: C.redBd, label: 'Match' },
  verified: { color: C.green, bg: C.greenBg, bd: C.greenBd, label: 'Verified' },
}

function Badge({ s, label }) {
  const m = SC[s] || SC.clear
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: m.bg, color: m.color, border: `1px solid ${m.bd}`, borderRadius: 20, padding: '2px 10px', fontSize: T.small, fontWeight: 700 }}>
      {label || m.label}
    </span>
  )
}

function Card({ children, style = {} }) {
  return <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 10, overflow: 'hidden', marginBottom: 16, ...style }}>{children}</div>
}

function Head({ title, sub, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 18px', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: T.normal, fontWeight: 800, color: '#1e293b' }}>{title}</div>
          {sub && <div style={{ fontSize: T.small, color: C.slateL }}>{sub}</div>}
        </div>
      </div>
      {action}
    </div>
  )
}

function TH({ children }) {
  return <th style={{ textAlign: 'left', padding: '9px 12px', fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{children}</th>
}

function useEvidence() {
  const [selected, setSelected] = useState([])
  const [showPicker, setShowPicker] = useState(false)
  const [targetCaseType, setTargetCaseType] = useState('INVESTIGATION')
  const [confirmed, setConfirmed] = useState(false)

  const toggle = (item) => {
    setSelected(prev =>
      prev.find(e => e.id === item.id)
        ? prev.filter(e => e.id !== item.id)
        : [...prev, item]
    )
    setConfirmed(false)
  }

  const isSelected = (id) => selected.some(e => e.id === id)
  const clear = () => { setSelected([]); setShowPicker(false); setTargetCaseType('INVESTIGATION'); setConfirmed(false) }

  return { selected, toggle, isSelected, clear, showPicker, setShowPicker, targetCaseType, setTargetCaseType, confirmed, setConfirmed }
}

function CctvModal({ egm, zone, date, time, arrivalTime, departureTime, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(7,56,74,0.6)', backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'relative', background: '#fff', borderRadius: 12, width: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        <div style={{ background: C.purpleBg, borderBottom: `1px solid ${C.purpleBd}`, padding: '18px 22px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Camera size={18} color={C.purple} />
              <span style={{ fontSize: 15, fontWeight: 800, color: C.purple }}>CCTV Footage Request</span>
            </div>
            <div style={{ fontSize: T.small, color: '#6b21a8' }}>Camera mapping information</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.purple, padding: 2 }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: '20px 22px' }}>
          <div style={{ background: C.amberBg, border: `1px solid ${C.amberBd}`, borderRadius: 8, padding: '12px 16px', marginBottom: 18, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <AlertTriangle size={16} color={C.amber} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: T.small, color: '#78350f', lineHeight: 1.6 }}>
              <strong>Camera mapping not yet configured.</strong> Once EGMs are mapped to base stations and cameras, this dialog will automatically show the specific cameras covering this location.
            </div>
          </div>
          
          <div style={{ background: C.slateBg, border: '1px solid #e8eef2', borderRadius: 8, padding: '14px 16px', marginBottom: 18 }}>
            <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Request Details</div>
            <div style={{ display: 'grid', gap: 10, fontSize: T.small }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: C.slateL }}>EGM Number:</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e293b' }}>{egm || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: C.slateL }}>Venue/Zone:</span>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{zone || '-'}</span>
              </div>
              {date && time && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: C.slateL }}>Transaction Time:</span>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{date} {time}</span>
                </div>
              )}
              {arrivalTime && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: C.slateL }}>Arrival:</span>
                  <span style={{ fontWeight: 600, color: C.green }}>{arrivalTime}</span>
                </div>
              )}
              {departureTime && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: C.slateL }}>Departure:</span>
                  <span style={{ fontWeight: 600, color: C.red }}>{departureTime}</span>
                </div>
              )}
            </div>
          </div>

          <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: 8, padding: '12px 16px', marginBottom: 18 }}>
            <div style={{ fontSize: T.small, fontWeight: 700, color: '#854d0e', marginBottom: 6 }}>Next Steps</div>
            <div style={{ fontSize: T.small, color: '#713f12', lineHeight: 1.6 }}>
              Contact security with the EGM number, venue, and time details above. They can use the venue floor plan to identify cameras covering that gaming area.
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={onClose}
              style={{ flex: 1, padding: '10px 0', background: C.purple, color: '#fff', border: 'none', borderRadius: 8, fontSize: T.normal, fontWeight: 800, cursor: 'pointer' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function EvidenceBar({ ev, memberId, onCreateCase }) {
  const { selected, clear, showPicker, setShowPicker, targetCaseType, setTargetCaseType, confirmed, setConfirmed } = ev
  if (selected.length === 0) return null

  const handleConfirm = async () => {
    const txIds = selected.filter(e => e.type === 'tx').map(e => String(e.id).replace(/^tx-/, ''))
    const sessIds = selected.filter(e => e.type === 'session').map(e => String(e.id).replace(/^sess-/, ''))
    await onCreateCase(targetCaseType, { transaction_ids: txIds, session_ids: sessIds })
    setConfirmed(true)
    setTimeout(() => { clear() }, 2000)
  }

  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      zIndex: 100, background: '#1e293b', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
      padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16,
      minWidth: 520, maxWidth: 700,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: T.normal, fontWeight: 800, color: '#fff', marginBottom: 3 }}>
          {selected.length} item{selected.length > 1 ? 's' : ''} selected
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {selected.slice(0, 5).map(e => (
            <span key={e.id} style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.12)', color: '#a0c4d4', padding: '2px 9px', borderRadius: 20 }}>
              {e.label?.slice(0, 30)}{e.label?.length > 30 ? '…' : ''}
            </span>
          ))}
          {selected.length > 5 && <span style={{ fontSize: 10, color: '#a0c4d4' }}>+{selected.length - 5} more</span>}
        </div>
      </div>

      {!showPicker && !confirmed && (
        <>
          <button onClick={() => setShowPicker(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#475569', color: '#fff', border: 'none', borderRadius: 7, fontSize: T.small, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Add to Case / Investigation <ArrowRight size={13} />
          </button>
          <button onClick={clear} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 4 }}>
            <X size={16} />
          </button>
        </>
      )}

      {showPicker && !confirmed && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={targetCaseType}
            onChange={e => setTargetCaseType(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 7, border: 'none', fontSize: T.small, fontWeight: 700, color: '#1e293b', minWidth: 200, cursor: 'pointer' }}
          >
            <option value="SMR">Create SMR</option>
            <option value="ECDD">Create ECDD</option>
            <option value="INVESTIGATION">Open Investigation</option>
          </select>
          <button onClick={handleConfirm} style={{ padding: '8px 16px', background: C.green, color: '#fff', border: 'none', borderRadius: 7, fontSize: T.small, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <CheckCircle2 size={13} /> Confirm
          </button>
          <button onClick={() => setShowPicker(false)} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
            <X size={15} />
          </button>
        </div>
      )}

      {confirmed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.greenBg, fontWeight: 800, fontSize: T.normal }}>
          <CheckCircle2 size={16} /> Case created ✓
        </div>
      )}
    </div>
  )
}

function RemoveModal({ patronName, patronId, addedBy, daysOnWatchlist, onClose, onConfirm }) {
  const [reasonCode, setReasonCode] = useState('')
  const [notes, setNotes] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const canSubmit = reasonCode && notes.trim().length >= 20 && acknowledged

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(7,56,74,0.6)', backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'relative', background: '#fff', borderRadius: 12, width: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        <div style={{ background: C.redBg, borderBottom: `1px solid ${C.redBd}`, padding: '18px 22px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <XCircle size={18} color={C.red} />
              <span style={{ fontSize: 15, fontWeight: 800, color: C.red }}>Remove from Watchlist</span>
            </div>
            <div style={{ fontSize: T.small, color: '#991b1b' }}>This action will be permanently logged in the audit trail.</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.red, padding: 2 }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: '20px 22px' }}>
          <div style={{ background: C.slateBg, border: '1px solid #e8eef2', borderRadius: 8, padding: '12px 16px', marginBottom: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.redBg, border: `2px solid ${C.redBd}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <User size={18} color={C.red} />
            </div>
            <div>
              <div style={{ fontSize: T.normal, fontWeight: 700, color: '#1e293b' }}>{patronName}</div>
              <div style={{ fontSize: T.small, color: C.slateL }}>{patronId} · Added by {addedBy || 'System'} · {daysOnWatchlist ?? 0} days on watchlist</div>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: T.small, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>Reason for removal <span style={{ color: C.red }}>*</span></div>
            <select
              value={reasonCode}
              onChange={e => setReasonCode(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', fontSize: T.normal, border: '1px solid #e2e8f0', borderRadius: 7, color: reasonCode ? '#1e293b' : C.slateL, background: '#fff', cursor: 'pointer', outline: 'none' }}
            >
              <option value="">— Select a reason —</option>
              {REMOVAL_REASONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: T.small, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
              Supporting notes <span style={{ color: C.red }}>*</span>
              <span style={{ fontSize: T.small, fontWeight: 400, color: C.slateL, marginLeft: 8 }}>Minimum 20 characters — this becomes part of the permanent audit record</span>
            </div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Describe the basis for removing this patron from the watchlist. What was found during the investigation or review that supports this decision?"
              style={{ width: '100%', minHeight: 90, fontSize: T.normal, border: `1px solid ${notes.length > 0 && notes.length < 20 ? C.amber : '#e2e8f0'}`, borderRadius: 7, padding: 10, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', color: '#334155', outline: 'none' }}
            />
            {notes.length > 0 && notes.length < 20 && (
              <div style={{ fontSize: 10, color: C.amber, marginTop: 4 }}>{20 - notes.length} more characters required</div>
            )}
          </div>
          <div
            onClick={() => setAcknowledged(v => !v)}
            style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', background: acknowledged ? C.greenBg : C.slateBg, border: `1px solid ${acknowledged ? C.greenBd : '#e2e8f0'}`, borderRadius: 8, cursor: 'pointer', marginBottom: 20 }}
          >
            <div style={{ marginTop: 1, flexShrink: 0 }}>
              {acknowledged ? <CheckSquare size={16} color={C.green} /> : <Square size={16} color={C.slateL} />}
            </div>
            <div style={{ fontSize: T.small, color: acknowledged ? '#166534' : C.slate, lineHeight: 1.5 }}>
              I confirm this removal is based on a documented review. I understand this action and its rationale will be permanently retained in the compliance audit log and cannot be deleted.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => canSubmit && onConfirm({ reasonCode, notes: notes.trim() })}
              style={{ flex: 1, padding: '10px 0', background: canSubmit ? C.red : '#e2e8f0', color: canSubmit ? '#fff' : C.slateL, border: 'none', borderRadius: 8, fontSize: T.normal, fontWeight: 800, cursor: canSubmit ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
            >
              <XCircle size={14} /> Remove from Watchlist
            </button>
            <button onClick={onClose} style={{ padding: '10px 20px', background: C.slateBg, color: C.slate, border: '1px solid #e2e8f0', borderRadius: 8, fontSize: T.normal, fontWeight: 700, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const TABS = [
  { key: 'patterns', label: 'Behaviour & Patterns', Icon: Activity },
  { key: 'transactions', label: 'Transactions', Icon: FileText },
  { key: 'kyc', label: 'KYC & AML Status', Icon: Fingerprint },
  { key: 'log', label: 'Observation Log', Icon: MessageSquare },
]

export default function WatchlistDetail() {
  const { memberId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isInactive = searchParams.get('inactive') === '1'
  const [tab, setTab] = useState('patterns')
  const [txSort, setTxSort] = useState('newest')
  const [txScope, setTxScope] = useState('all')
  const [txMinCashOut, setTxMinCashOut] = useState('')
  const [showRemove, setShowRemove] = useState(false)
  const [removed, setRemoved] = useState(false)
  const [overview, setOverview] = useState({ loading: true, data: null, error: null })
  const [memberNotes, setMemberNotes] = useState([])
  const [newNoteText, setNewNoteText] = useState('')
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const noteFileInputRef = useRef(null)
  const [openCases, setOpenCases] = useState([])
  const [amlChecks, setAmlChecks] = useState([])
  const [amlLoading, setAmlLoading] = useState(false)
  const [amlModalOpen, setAmlModalOpen] = useState(false)
  const [amlModalData, setAmlModalData] = useState(null)
  const [amlReviewSaving, setAmlReviewSaving] = useState(false)
  const [cctvModalOpen, setCctvModalOpen] = useState(false)
  const [cctvModalData, setCctvModalData] = useState(null)
  const [visitPattern, setVisitPattern] = useState(null)
  const [gamingBehaviour, setGamingBehaviour] = useState(null)
  const ev = useEvidence()

  useEffect(() => {
    if (!memberId) return
    let cancelled = false
    authFetch(getLocalApiUrl(`/api/members/${encodeURIComponent(memberId)}/visit-pattern?months=12`))
      .then(r => r.json()).then(j => { if (!cancelled && j.success) setVisitPattern(j.data) }).catch(() => {})
    authFetch(getLocalApiUrl(`/api/members/${encodeURIComponent(memberId)}/gaming-behaviour?months=12`))
      .then(r => r.json()).then(j => { if (!cancelled && j.success) setGamingBehaviour(j.data) }).catch(() => {})
    return () => { cancelled = true }
  }, [memberId])

  const loadOverview = useCallback(async () => {
    if (!memberId) return
    try {
      setOverview((prev) => ({ ...prev, loading: true, error: null }))
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/overview`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load')
      setOverview({ loading: false, data: json.data, error: null })
      setOpenCases((json.data?.cases || []).filter((c) => {
        const st = String(c.status || '').toLowerCase()
        return ['open', 'pending', 'investigating', 'suspicion_formed', 'smr_draft', 'pending_review', 'pending_approval'].includes(st)
      }))
    } catch (err) {
      setOverview({ loading: false, data: null, error: err.message })
    }
  }, [memberId])

  const loadNotes = useCallback(async () => {
    if (!memberId) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/notes`))
      const json = await res.json()
      if (json.success) setMemberNotes(json.data || [])
    } catch (_) { setMemberNotes([]) }
  }, [memberId])

  const loadAmlChecks = useCallback(async () => {
    if (!memberId) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/aml/checks?member_id=${encodeURIComponent(memberId)}&limit=10`))
      const json = await res.json()
      if (json?.success && Array.isArray(json.data)) setAmlChecks(json.data)
    } catch (_) { setAmlChecks([]) }
  }, [memberId])

  const profile = overview.data?.profile || {}
  const patronName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || memberId || 'Unknown'
  const kycAml = overview.data?.kyc_aml || {}
  const kycCompleted = !!kycAml.kyc_completed
  const dvsStatus = (kycAml.dvs_status || 'PENDING').toUpperCase()
  const dvsVerified = ['VERIFIED', 'MATCHED', 'PASSED'].includes(dvsStatus)

  const handleReScreen = useCallback(async () => {
    const name = patronName.trim()
    if (!memberId || !name || name === 'Unknown') return
    try {
      setAmlLoading(true)
      const res = await authFetch(getLocalApiUrl('/api/aml/checks/run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: memberId,
          name,
          country: 'AUST',
          reason: 'Watchlist re-screen',
          performed_by: 'web_user'
        })
      })
      const json = await res.json()
      if (!json?.success) throw new Error(json?.error || 'AML check failed')
      await loadAmlChecks()
      await loadOverview()
      const d = json.data
      if (d?.id) {
        setAmlModalData({
          checkId: d.id,
          name: d.name || patronName,
          country: d.country,
          results: Array.isArray(d.sample) ? d.sample : [],
          resultsByCategory: d.resultsByCategory || null,
          analystDecision: null,
          analystReason: '',
        })
        setAmlModalOpen(true)
      }
    } catch (e) {
      alert(e?.message || 'Re-screen failed')
    } finally {
      setAmlLoading(false)
    }
  }, [memberId, patronName, loadAmlChecks, loadOverview])

  const [riskLoading, setRiskLoading] = useState(false)
  const [creatingEcdd, setCreatingEcdd] = useState(false)
  const [creatingSmr, setCreatingSmr] = useState(false)
  const [lastRecalcScore, setLastRecalcScore] = useState(null)
  const [lastRecalcLevel, setLastRecalcLevel] = useState(null)
  const [lastRecalcCddTier, setLastRecalcCddTier] = useState(null)
  const [headerCddTier, setHeaderCddTier] = useState(null)
  const handleRecalculateRisk = useCallback(async () => {
    if (!memberId) return
    try {
      setRiskLoading(true)
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/recalculate-risk`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      })
      const json = await res.json()
      if (!json?.success) throw new Error(json?.error || 'Recalculate failed')
      const recalcScore = json.data?.risk_score
      const recalcLevel = json.data?.risk_level
      const recalcCddTier = json.data?.cdd_tier
      if (recalcScore != null) setLastRecalcScore(recalcScore)
      if (recalcLevel != null) setLastRecalcLevel(recalcLevel)
      if (recalcCddTier != null) setLastRecalcCddTier(recalcCddTier)
      await loadOverview()
      setOverview(prev => {
        if (!prev?.data) return prev
        return {
          ...prev,
          data: {
            ...prev.data,
            ...(recalcScore != null ? { risk_score: recalcScore } : {}),
            ...(recalcLevel != null ? { risk_level: recalcLevel } : {}),
            ...(recalcCddTier != null ? { cdd_tier: recalcCddTier } : {}),
          },
        }
      })
    } catch (e) {
      alert(e?.message || 'Recalculate risk failed')
    } finally {
      setRiskLoading(false)
    }
  }, [memberId, loadOverview])

  const viewAmlCheckDetails = useCallback(async (checkId) => {
    if (!checkId) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/aml/checks/${checkId}`))
      const json = await res.json()
      if (!json?.success || !json.data) return
      const d = json.data
      const sample = Array.isArray(d.result_sample) ? d.result_sample : []
      setAmlModalData({
        checkId: d.id,
        name: d.full_name || patronName,
        country: d.country_code,
        results: sample,
        analystDecision: d.analyst_decision || null,
        analystReason: d.analyst_reason || '',
      })
      setAmlModalOpen(true)
    } catch (_) {}
  }, [patronName])

  useEffect(() => { loadOverview() }, [loadOverview])
  useEffect(() => { setLastRecalcScore(null); setLastRecalcLevel(null); setLastRecalcCddTier(null); setHeaderCddTier(null) }, [memberId])
  useEffect(() => { loadNotes() }, [loadNotes])
  useEffect(() => { loadAmlChecks() }, [loadAmlChecks])

  useEffect(() => {
    const fs = overview.data?.watchlist?.first_seen
    if (!fs && txScope === 'since_watchlist') setTxScope('all')
  }, [overview.data?.watchlist?.first_seen, txScope])

  const transactions = overview.data?.transactions || []
  const sessions = overview.data?.sessions || []
  const alerts = overview.data?.alerts || []
  const watchlist = overview.data?.watchlist || {}
  const firstAlert = alerts[0]

  const addedAt = watchlist?.first_seen ? new Date(watchlist.first_seen).getTime() : null

  /** Rows from gaming_transactions: EGM meter snapshots (periodic reads), not individual payout tickets. */
  const meterSnapshots = transactions.map(t => {
    const d = t.snapshot_time ? new Date(t.snapshot_time) : null
    const ts = d ? d.getTime() : 0
    const cashIn = Number(t.CashIn ?? t.cashin ?? 0)
    const cashOut = Number(t.CashOut ?? t.cashout ?? 0)
    return {
      id: t.transaction_id || t.TransactionPK,
      date: d ? d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : '-',
      time: d ? d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '-',
      cashIn,
      cashOut,
      turnover: Number(t.Turnover ?? t.turnover ?? 0),
      egm: t.egm_house_no || t.EgmHouseNo || '-',
      zone: t.venue || '-',
      flagHighCashOut: cashOut >= 5000,
      _ts: ts,
    }
  })

  const minCo = txMinCashOut.trim() === '' ? null : Number(txMinCashOut)
  const meterSnapshotsFiltered = (() => {
    let rows = [...meterSnapshots]
    if (txScope === 'since_watchlist' && addedAt) rows = rows.filter(m => m._ts >= addedAt)
    if (minCo != null && !Number.isNaN(minCo)) rows = rows.filter(m => m.cashOut >= minCo)
    rows.sort((a, b) => (txSort === 'oldest' ? a._ts - b._ts : b._ts - a._ts))
    return rows
  })()

  const sessionRows = sessions.map(s => {
    const start = s.start_time ? new Date(s.start_time) : null
    const end = s.end_time ? new Date(s.end_time) : null
    const durMs = start && end ? end - start : 0
    const durM = Math.floor(durMs / 60000)
    const dur = durM >= 60 ? `${Math.floor(durM / 60)}h ${durM % 60}m` : `${durM}m`
    const cashIn = s.CashIn ?? 0
    const creditWin = s.credit_win ?? s.CreditWin ?? 0
    const turnover = s.Turnover ?? s.turnover ?? 0
    const strokes = s.Stroke ?? s.stroke ?? 0
    return {
      id: s.SessionPK || s.sessionpk,
      date: start ? start.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
      timeIn: start ? start.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '-',
      timeOut: end ? end.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '-',
      dur,
      egm: s.egmhouseno || s.egm_house_no || s.EgmHouseNo || '-',
      zone: s.venue || '-',
      cashIn,
      creditWin,
      turnover,
      strokes,
      net: creditWin - cashIn,
      _startTs: start ? start.getTime() : 0,
    }
  })

  const sessionRowsFiltered = (() => {
    if (txScope !== 'since_watchlist' || !addedAt) return sessionRows
    return sessionRows.filter(s => s._startTs >= addedAt)
  })()

  // Calculate gaming behavior statistics (shared across tabs)
  const avgSessionDuration = sessionRows.length > 0 
    ? (() => {
        const totalMinutes = sessionRows.reduce((sum, s) => {
          const start = s.timeIn ? new Date(`2000-01-01 ${s.timeIn}`) : null
          const end = s.timeOut ? new Date(`2000-01-01 ${s.timeOut}`) : null
          if (!start || !end) return sum
          const diffMs = end - start
          return sum + (diffMs > 0 ? diffMs / 60000 : 0)
        }, 0)
        const avgMins = Math.round(totalMinutes / sessionRows.length)
        const hours = Math.floor(avgMins / 60)
        const mins = avgMins % 60
        return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
      })()
    : '—'
  
  const avgCreditWin = sessionRows.length > 0 
    ? fmt$(Math.round(sessionRows.reduce((a, s) => a + (s.creditWin || 0), 0) / sessionRows.length))
    : '—'
  
  const peakArrivalTime = sessionRows.length > 0
    ? (() => {
        const hourCounts = {}
        sessionRows.forEach(s => {
          if (!s.timeIn) return
          const hour = parseInt(s.timeIn.split(':')[0])
          if (!isNaN(hour)) {
            hourCounts[hour] = (hourCounts[hour] || 0) + 1
          }
        })
        if (Object.keys(hourCounts).length === 0) return '—'
        const sortedHours = Object.entries(hourCounts).sort((a, b) => b[1] - a[1])
        const topHour = parseInt(sortedHours[0][0])
        const formatHour = (h) => h > 12 ? `${h-12}pm` : h === 12 ? '12pm' : `${h}am`
        return `${formatHour(topHour)} – ${formatHour(topHour + 2)}`
      })()
    : '—'
  
  const avgTurnover = sessionRows.length > 0
    ? fmt$(Math.round(sessionRows.reduce((a, s) => a + (s.turnover || 0), 0) / sessionRows.length))
    : '—'

  const startSmr = useCallback(async (opts = {}) => {
    if (!memberId || creatingSmr) return
    setCreatingSmr(true)
    try {
      const goToSmrCase = (caseNum) => {
        const num = String(caseNum || '').trim()
        if (!num) throw new Error('No investigation case number returned')
        navigate(`/compliance-safe/investigations/${encodeURIComponent(num)}?tab=smr_form`)
      }

      const existing = openCases.find((c) => c.case_number || c.id)
      if (existing?.case_number || existing?.id) {
        goToSmrCase(existing.case_number || existing.id)
        return
      }

      const payload = {
        transaction_ids: opts.transaction_ids || [],
        session_ids: opts.session_ids || [],
        suspicion: {},
        nature: null,
        notes: 'SMR started from watchlist',
        suspected_tf: false,
        case_type: 'INVESTIGATION',
      }
      if (firstAlert?.id) payload.alert_id = firstAlert.id
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/cases`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to start SMR')
      const caseNum = json.case?.case_number || json.data?.case_number || ''
      goToSmrCase(caseNum)
    } catch (e) {
      alert(e.message || 'Failed to start SMR')
    } finally {
      setCreatingSmr(false)
    }
  }, [memberId, creatingSmr, openCases, firstAlert?.id, navigate])

  const createCase = async (caseType, opts = {}) => {
    if (caseType === 'SMR') return startSmr(opts)
    if (!memberId) return
    try {
      const payload = {
        transaction_ids: opts.transaction_ids || [],
        session_ids: opts.session_ids || [],
        suspicion: {},
        nature: null,
        notes: 'Created from watchlist detail',
        suspected_tf: false,
        case_type: caseType,
      }
      if (firstAlert?.id) payload.alert_id = firstAlert.id
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/cases`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to create case')
      const caseNum = json.case?.case_number || json.data?.case_number || ''
      if (caseType === 'INVESTIGATION' && caseNum) {
        navigate(`/compliance-safe/investigations/${encodeURIComponent(caseNum)}`)
      } else {
        alert(`${caseType} case created: ${caseNum}`)
        loadOverview()
      }
    } catch (e) {
      alert(e.message || 'Failed to create case')
    }
  }

  const openEcdd = async () => {
    if (!memberId || creatingEcdd) return
    setCreatingEcdd(true)
    try {
      const venueFromActivity =
        overview.data?.sessions?.[0]?.venue ||
        overview.data?.transactions?.[0]?.venue ||
        null
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/ecdd`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venue_name: venueFromActivity || undefined,
          trigger_description: `Opened from watchlist${firstAlert?.ref ? ` (alert ${firstAlert.ref})` : ''}`,
          alert_id: firstAlert?.id ?? undefined,
        }),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to create ECDD')
      const ecddId = json.data?.ecdd_id || json.data?.id
      if (!ecddId) throw new Error('Server did not return an ECDD id')
      if (json.data?.open_ecdd_warning) alert(json.data.open_ecdd_warning)
      const q = new URLSearchParams()
      if (profile?.master_profile_id) q.set('patronId', profile.master_profile_id)
      q.set('memberId', memberId)
      if (patronName && patronName !== memberId) q.set('memberName', patronName)
      const qs = q.toString()
      navigate(`/compliance-safe/ecdd/${encodeURIComponent(ecddId)}${qs ? `?${qs}` : ''}`)
    } catch (e) {
      alert(e.message || 'Failed to create ECDD')
    } finally {
      setCreatingEcdd(false)
    }
  }

  const reAddToWatchlist = async () => {
    if (!memberId) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/reactivate`), { method: 'POST' })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to re-add')
      navigate(`/monitor-safe/watch-list/${encodeURIComponent(memberId)}`)
    } catch (e) {
      alert(e.message || 'Failed to re-add')
    }
  }

  const handleRemove = async ({ reasonCode, notes }) => {
    if (!memberId) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        // S8-F1a: backend expects structured removal_reason_code + optional
        // narrative. Existing 20-char notes minimum satisfies the backend's
        // "other"-only narrative requirement automatically.
        body: JSON.stringify({ removal_reason_code: reasonCode, reason: notes }),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to remove')
      setShowRemove(false)
      setRemoved(true)
    } catch (e) {
      alert(e.message || 'Failed to remove')
    }
  }

  const addNote = async () => {
    if (!memberId || !newNoteText.trim()) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/notes`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({ note: newNoteText.trim() }),
      })
      const json = await res.json()
      if (json.success) {
        setNewNoteText('')
        loadNotes()
      } else throw new Error(json.error)
    } catch (e) {
      alert(e.message || 'Failed to add note')
    }
  }

  const handleFileAttachment = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !memberId) return
    
    setUploadingAttachment(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      
      const res = await authFetch(
        getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/attachments`),
        { method: 'POST', body: fd }
      )
      
      if (res.ok) {
        const json = await res.json()
        if (json.success) {
          alert(`File "${file.name}" uploaded successfully`)
          // Could reload attachments list here if you have one
        } else {
          throw new Error(json.error || 'Upload failed')
        }
      } else {
        throw new Error('Upload failed')
      }
    } catch (err) {
      console.error('File upload error:', err)
      alert(err.message || 'Failed to upload file')
    } finally {
      setUploadingAttachment(false)
      e.target.value = ''
    }
  }

  const totalMeterCashOut = meterSnapshots.reduce((a, m) => a + m.cashOut, 0)
  const totalSessionCashIn = sessionRows.reduce((a, s) => a + (s.cashIn || 0), 0)

  const now = Date.now()
  const weekMs = 7 * 86400000

  // 12-week payout: use DB-driven payout_trend_12w from API when available, else compute from transactions + sessions
  const apiPayoutTrend = overview.data?.payout_trend_12w
  const payoutWeeks = Array.isArray(apiPayoutTrend) && apiPayoutTrend.some(v => v > 0)
    ? apiPayoutTrend
    : (() => {
        return Array(12).fill(0).map((_, i) => {
          const weekEnd = now - i * weekMs
          const weekStart = weekEnd - weekMs
          let sum = 0
          transactions.forEach(t => {
            const ts = t.snapshot_time ? new Date(t.snapshot_time).getTime() : 0
            if (ts >= weekStart && ts < weekEnd) sum += Number(t.CashOut ?? t.cashout ?? 0)
          })
          sessions.forEach(s => {
            const ts = s.start_time ? new Date(s.start_time).getTime() : 0
            if (ts >= weekStart && ts < weekEnd) sum += Number(s.credit_win ?? s.CreditWin ?? 0)
          })
          return sum
        }).reverse()
      })()

  // Visit pattern — prefer 12-month API history, fall back to current session window
  const dowNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const visitDow = visitPattern?.has_data
    ? dowNames.map((dayName, i) => {
        const fromApi = (visitPattern.dow_counts || []).find((v) => v?.day === dayName)
          || visitPattern.dow_counts?.[i]
        const n = Number(fromApi?.count ?? fromApi?.n ?? 0)
        return { d: dayName, n: Number.isFinite(n) ? n : 0 }
      })
    : (() => {
        const counts = dowNames.map(() => 0)
        sessions.forEach(s => {
          const d = s.start_time ? new Date(s.start_time).getDay() : 0
          const idx = d === 0 ? 6 : d - 1
          counts[idx] += 1
        })
        return dowNames.map((d, i) => ({ d, n: counts[i] }))
      })()

  const maxDow = Math.max(...visitDow.map(v => v.n), 1)
  const maxWeek = Math.max(...payoutWeeks, 1)

  if (overview.loading && !overview.data) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: C.slate }}>
        Loading watchlist detail…
      </div>
    )
  }

  if (overview.error) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: C.red }}>
        {overview.error}
        <div style={{ marginTop: 16 }}>
          <Link to="/monitor-safe/watch-list" style={{ color: '#475569', fontWeight: 600 }}>← Back to Watchlist</Link>
        </div>
      </div>
    )
  }

  return (
    <div style={{ fontFamily: "'Poppins', system-ui, sans-serif", minHeight: '100vh', padding: '16px 24px', paddingBottom: ev.selected.length > 0 ? 100 : 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Link to="/monitor-safe/watch-list" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: T.normal, color: C.slate, textDecoration: 'none', fontWeight: 600 }}>
          <ChevronLeft size={16} /> Back to Watchlist
        </Link>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => createCase('INVESTIGATION', { alert_id: firstAlert?.id })}
          style={{ padding: '8px 16px', background: '#fff', color: C.slate, border: '1px solid #e2e8f0', borderRadius: 7, fontSize: T.small, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Search size={13} /> Investigation
        </button>
        <button
          type="button"
          onClick={openEcdd}
          disabled={creatingEcdd}
          style={{ padding: '8px 16px', background: C.purpleBg, color: C.purple, border: `1px solid ${C.purpleBd}`, borderRadius: 7, fontSize: T.small, fontWeight: 700, cursor: creatingEcdd ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: creatingEcdd ? 0.7 : 1 }}
        >
          <Shield size={13} /> {creatingEcdd ? 'Opening…' : 'Open ECDD'}
        </button>
        <button
          type="button"
          onClick={() => startSmr()}
          disabled={creatingSmr}
          style={{ padding: '8px 16px', background: C.redBg, color: C.red, border: `1px solid ${C.redBd}`, borderRadius: 7, fontSize: T.small, fontWeight: 700, cursor: creatingSmr ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: creatingSmr ? 0.7 : 1 }}
        >
          <FileText size={13} /> {creatingSmr ? 'Opening…' : 'Start SMR'}
        </button>
        <button
          type="button"
          onClick={() => setShowRemove(true)}
          disabled={removed}
          style={{ padding: '8px 16px', background: removed ? C.slateBg : C.redBg, color: removed ? C.slateL : C.red, border: `1px solid ${removed ? '#e2e8f0' : C.redBd}`, borderRadius: 7, fontSize: T.small, fontWeight: 700, cursor: removed ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <XCircle size={13} /> {removed ? 'Removed' : 'Remove from Watchlist'}
        </button>
        <button
          type="button"
          disabled={riskLoading}
          onClick={handleRecalculateRisk}
          style={{ padding: '8px 16px', background: riskLoading ? C.slateBg : C.slateBg, color: riskLoading ? C.slateL : C.slate, border: '1px solid #e2e8f0', borderRadius: 7, fontSize: T.small, fontWeight: 700, cursor: riskLoading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: riskLoading ? 0.7 : 1 }}
        >
          <RefreshCw size={13} /> {riskLoading ? 'Recalculating…' : 'Recalculate risk'}
        </button>
        </div>
      </div>

      {/* Patron header — teal/green panel (reference design) */}
      <div style={{ background: C.pri, padding: '20px 24px 0', borderRadius: '10px 10px 0 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: C.redBg, border: '3px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <User size={24} color={C.red} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <Eye size={13} color="#fbbf24" />
                <span style={{ fontSize: T.small, fontWeight: 700, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: 1 }}>Watchlist — Active Monitoring</span>
                {watchlist.first_seen && (
                  <span style={{ fontSize: T.small, fontWeight: 700, color: '#fff', background: C.red, padding: '1px 10px', borderRadius: 20 }}>
                    {Math.floor((Date.now() - new Date(watchlist.first_seen).getTime()) / 86400000)} days on watchlist
                  </span>
                )}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 6 }}>{patronName}</div>
              <div style={{ fontSize: T.small, color: '#a0c4d4', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                {profile?.master_profile_id && (
                  <span style={{ fontFamily: 'monospace', background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: 4 }}>{profile.master_profile_id}</span>
                )}
                {(displayMembershipNumber(profile) || profile?.membership_number) && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Member #">
                    <Users size={11} />
                    <span style={{ fontFamily: 'monospace', background: 'rgba(59,130,246,0.2)', color: '#93c5fd', padding: '2px 8px', borderRadius: 4 }}>
                      {displayMembershipNumber(profile) || profile.membership_number}
                    </span>
                  </span>
                )}
                {displayPlayerId(profile) && displayPlayerId(profile) !== displayMembershipNumber(profile) && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Player ID">
                    <span style={{ fontSize: 10, color: '#a0c4d4' }}>IGT</span>
                    <span style={{ fontFamily: 'monospace', background: 'rgba(255,255,255,0.1)', color: '#cbd5e1', padding: '2px 8px', borderRadius: 4 }}>
                      {displayPlayerId(profile)}
                    </span>
                  </span>
                )}
                {!displayMembershipNumber(profile) && !profile?.membership_number && profile?.member_id && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Member #">
                    <Users size={11} />
                    <span style={{ fontFamily: 'monospace', background: 'rgba(59,130,246,0.2)', color: '#93c5fd', padding: '2px 8px', borderRadius: 4 }}>{profile.member_id}</span>
                  </span>
                )}
                {profile?.visitor_id && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <User size={11} />
                    <span style={{ fontFamily: 'monospace', background: 'rgba(251,191,36,0.2)', color: '#fcd34d', padding: '2px 8px', borderRadius: 4 }}>{profile.visitor_id}</span>
                  </span>
                )}
                {!profile?.master_profile_id && !profile?.member_id && !profile?.visitor_id && (
                  <span style={{ fontFamily: 'monospace' }}>{memberId}</span>
                )}
                {profile?.occupation ? (
                  <>
                    <span style={{ color: 'rgba(160,196,212,0.6)' }}>·</span>
                    <span>{profile.occupation}</span>
                  </>
                ) : null}
              </div>
              <div className="mb-1">
                <PatronComplianceStrip
                  size="compact"
                  dark
                  complianceStage={overview.data?.compliance_stage}
                  riskScore={overview.data?.risk_score ?? lastRecalcScore}
                  riskLevel={overview.data?.risk_level ?? lastRecalcLevel}
                  cddTier={overview.data?.cdd_tier ?? lastRecalcCddTier ?? headerCddTier}
                />
              </div>
            </div>
          </div>
          {watchlist.review_due && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#a0c4d4', background: 'rgba(255,255,255,0.08)', padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.12)', flexShrink: 0, alignSelf: 'flex-start' }}>
              Review due: {new Date(watchlist.review_due).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>

        {removed && (
          <div style={{ background: C.greenBg, border: `1px solid ${C.greenBd}`, borderRadius: 8, padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
            <CheckCircle2 size={15} color={C.green} style={{ flexShrink: 0 }} />
            <div style={{ fontSize: T.small, color: '#166534' }}>
              <strong>Removed from watchlist.</strong> Record and rationale have been logged. Profile remains in Profile Safe.
            </div>
          </div>
        )}

        {isInactive && !removed && (
          <div style={{ background: C.greenBg, border: `1px solid ${C.greenBd}`, borderRadius: 8, padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: T.small, color: '#166534' }}>
              <strong>This member was removed from the watchlist.</strong> Re-add to resume monitoring.
            </div>
            <button onClick={reAddToWatchlist} style={{ padding: '8px 16px', background: C.pri, color: '#fff', border: 'none', borderRadius: 7, fontSize: T.small, fontWeight: 700, cursor: 'pointer' }}>
              Re-add to Watchlist
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginBottom: 0, flexWrap: 'wrap' }}>
          {firstAlert?.ref && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#a0c4d4', background: 'rgba(255,255,255,0.08)', padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.12)' }}>
              Alert: {firstAlert.ref}
            </span>
          )}
          {openCases.slice(0, 3).map(c => (
            <button
              key={c.id || c.case_number}
              onClick={() => navigate(`/compliance-safe/investigations/${encodeURIComponent(c.case_number || c.id)}`)}
              style={{ fontSize: 11, fontWeight: 700, color: '#a0c4d4', background: 'rgba(255,255,255,0.08)', padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer' }}
            >
              Investigation: {c.case_number || c.id}
            </button>
          ))}
          {watchlist.added_by && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#a0c4d4', background: 'rgba(255,255,255,0.08)', padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.12)' }}>
              Added by: {watchlist.added_by}
            </span>
          )}
        </div>

        {/* Tabs — inside the green panel */}
        <div style={{ display: 'flex', marginTop: 18 }}>
          {TABS.map(t => {
            const active = tab === t.key
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: T.small, fontWeight: 700, background: active ? '#fff' : 'transparent', color: active ? C.pri : '#a0c4d4', borderRadius: '8px 8px 0 0', transition: 'all 0.15s' }}
              >
                <t.Icon size={13} /> {t.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Body — content below the green panel, end to end */}
      <div style={{ padding: '24px 24px', marginLeft: -24, marginRight: -24, width: 'calc(100% + 48px)', boxSizing: 'border-box' }}>
        {memberId && (
          <div style={{ marginBottom: 12 }}>
            <CddTierCard memberId={memberId} dense onTierLoaded={setHeaderCddTier} />
          </div>
        )}
        {tab === 'transactions' && (
          <>
            {(() => {
              const topEgms = gamingBehaviour?.has_data && (gamingBehaviour.top_egms || []).length > 0
                ? gamingBehaviour.top_egms.slice(0, 3).map(e => ({ egm: e.egm_pk || '—', sessions: e.session_count, total: e.total_credit_win ?? 0, pct: e.pct }))
                : (() => {
                    const egmCounts = {}
                    sessionRows.forEach(s => {
                      const egm = s.egm || '-'
                      if (!egmCounts[egm]) egmCounts[egm] = { sessions: 0, total: 0 }
                      egmCounts[egm].sessions += 1
                      egmCounts[egm].total += s.creditWin || 0
                    })
                    const prefs = Object.entries(egmCounts).map(([egm, d]) => ({ egm, sessions: d.sessions, total: d.total, pct: 0 }))
                    const maxS = Math.max(...prefs.map(e => e.sessions), 1)
                    prefs.forEach(e => { e.pct = Math.round((e.sessions / maxS) * 100) })
                    return prefs.sort((a, b) => b.sessions - a.sessions).slice(0, 3)
                  })()
              const behAvgDur = gamingBehaviour?.avg_duration_min != null
                ? (gamingBehaviour.avg_duration_min >= 60
                    ? `~${Math.floor(gamingBehaviour.avg_duration_min / 60)}h ${gamingBehaviour.avg_duration_min % 60}m`
                    : `~${gamingBehaviour.avg_duration_min}m`)
                : avgSessionDuration
              const behPeak = gamingBehaviour?.peak_hour_label ?? peakArrivalTime
              const behTotalSessions = gamingBehaviour?.total_sessions ?? sessionRows.length

              return (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
                  <Card style={{ marginBottom: 0 }}>
                    <Head title="Gaming Behaviour Profile" sub={gamingBehaviour?.has_data ? '12-month history' : 'current window'} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
                      {[
                        ['Avg session length', behAvgDur, false],
                        ['Avg turnover/session', avgTurnover, false],
                        ['Peak arrival time', behPeak, false],
                        ['Zone preference', sessionRows[0]?.zone || '—', false],
                        ['Total sessions (12 mo)', behTotalSessions, false],
                      ].map(([l, v, flag], i, arr) => (
                        <div key={i} style={{ padding: '13px 16px', borderBottom: i < arr.length - 3 ? '1px solid #f8f8f8' : 'none', borderRight: i % 3 !== 2 ? '1px solid #f8f8f8' : 'none' }}>
                          <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>{l}</div>
                          <div style={{ fontSize: T.normal, fontWeight: 800, color: flag ? C.red : '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {v} {flag && <AlertTriangle size={12} color={C.amber} />}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                  <Card style={{ marginBottom: 0 }}>
                    <Head title="EGM Preferences" sub={gamingBehaviour?.has_data ? 'Top 3 · 12 months' : 'Top 3 · current window'} />
                    <div style={{ padding: '14px 18px' }}>
                      {topEgms.length === 0 ? (
                        <div style={{ fontSize: T.small, color: C.slateL }}>No session data yet</div>
                      ) : (
                        topEgms.map((e, i) => (
                          <div key={e.egm} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: i < topEgms.length - 1 ? '1px solid #f8f8f8' : 'none' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: T.normal, fontWeight: 800, color: i === 0 ? C.red : '#1e293b', width: 60, flexShrink: 0 }}>{e.egm}</span>
                            <div style={{ flex: 1, height: 7, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${e.pct}%`, background: i === 0 ? C.red : i === 1 ? C.amber : C.priL, borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: T.small, fontWeight: 700, color: '#1e293b', minWidth: 60, textAlign: 'right' }}>{e.sessions} visits</span>
                          </div>
                        ))
                      )}
                    </div>
                  </Card>
                </div>
              )
            })()}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: T.small, fontWeight: 700, color: C.slate }}>Sort</span>
                <select value={txSort} onChange={e => setTxSort(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: T.small }}>
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: T.small, fontWeight: 700, color: C.slate }}>Scope</span>
                <select value={txScope} onChange={e => setTxScope(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: T.small }}>
                  <option value="all">All snapshots (window)</option>
                  <option value="since_watchlist" disabled={!addedAt}>Since added to watchlist</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: T.small, fontWeight: 700, color: C.slate }}>Min cash out ($)</span>
                <input
                  type="number"
                  min={0}
                  placeholder="Any"
                  value={txMinCashOut}
                  onChange={e => setTxMinCashOut(e.target.value)}
                  style={{ width: 100, padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: T.small }}
                />
              </div>
              <span style={{ fontSize: T.small, color: C.slateL }}>{meterSnapshotsFiltered.length} snapshot{meterSnapshotsFiltered.length === 1 ? '' : 's'} · {sessionRowsFiltered.length} session{sessionRowsFiltered.length === 1 ? '' : 's'}</span>
            </div>

            <Card>
              <Head title="EGM meter snapshots" sub="Cash in / cash out / turnover at snapshot time — tick rows to attach as evidence" />
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.small }}>
                  <thead>
                    <tr style={{ background: '#fafbfc', borderBottom: '1px solid #e8eef2' }}>
                      <TH></TH><TH></TH><TH>ID</TH><TH>Date</TH><TH>Time</TH><TH>Cash in</TH><TH>Cash out</TH><TH>Turnover</TH><TH>EGM</TH><TH>Zone</TH><TH></TH>
                    </tr>
                  </thead>
                  <tbody>
                    {meterSnapshotsFiltered.length === 0 && (
                      <tr><td colSpan={11} style={{ padding: 16, color: C.slateL }}>No meter snapshots match your filters.</td></tr>
                    )}
                    {meterSnapshotsFiltered.map((p, pi) => {
                      const sel = ev.isSelected(`tx-${p.id}`)
                      return (
                        <tr key={p.id ?? `tx-${pi}`} style={{ borderBottom: '1px solid #f8f8f8', background: sel ? '#f1f5f9' : p.flagHighCashOut ? '#fffbf0' : '#fff' }}>
                          <td style={{ padding: '10px 8px 10px 14px' }}>
                            <div style={{ cursor: 'pointer' }} onClick={() => ev.toggle({ id: `tx-${p.id}`, type: 'tx', label: `Meter ${p.id} — out ${fmt$(p.cashOut)} on ${p.date}` })}>
                              {sel ? <CheckSquare size={15} color="#475569" /> : <Square size={15} color={C.slateL} />}
                            </div>
                          </td>
                          <td style={{ padding: '10px 4px' }}>{p.flagHighCashOut && <AlertTriangle size={13} color={C.amber} title="High cash out" />}</td>
                          <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: T.small, color: '#475569', fontWeight: 700 }}>{p.id}</td>
                          <td style={{ padding: '10px 12px', fontWeight: 600, color: '#334155' }}>{p.date}</td>
                          <td style={{ padding: '10px 12px', color: C.slate }}>{p.time}</td>
                          <td style={{ padding: '10px 12px', color: '#334155' }}>{fmt$(p.cashIn)}</td>
                          <td style={{ padding: '10px 12px', fontWeight: 800, color: p.cashOut >= 5000 ? C.red : '#1e293b' }}>{fmt$(p.cashOut)}</td>
                          <td style={{ padding: '10px 12px', color: C.slate }}>{fmt$(p.turnover)}</td>
                          <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 700 }}>{p.egm}</td>
                          <td style={{ padding: '10px 12px', color: C.slate }}>{p.zone}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <button 
                              type="button"
                              onClick={() => setCctvModalOpen(true) || setCctvModalData({ egm: p.egm, zone: p.zone, date: p.date, time: p.time })}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 9px', fontSize: T.small, fontWeight: 700, color: C.purple, background: C.purpleBg, border: `1px solid ${C.purpleBd}`, borderRadius: 6, cursor: 'pointer' }}
                            >
                              <Camera size={10} /> View
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card>
              <Head title="Gaming sessions" sub="Per-visit summary — tick to add as evidence" />
              <div style={{ padding: '0 18px 4px' }}>
                <div style={{ background: C.amberBg, border: `1px solid ${C.amberBd}`, borderRadius: 7, padding: '9px 14px', margin: '12px 0', display: 'flex', gap: 8, alignItems: 'center', fontSize: T.small, color: '#78350f' }}>
                  <Camera size={13} color={C.amber} /> Use exact arrival time + EGM when requesting footage from security.
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.small }}>
                  <thead>
                    <tr style={{ background: '#fafbfc', borderBottom: '1px solid #e8eef2' }}>
                      <TH></TH><TH>Date</TH><TH>Arrived</TH><TH>Departed</TH><TH>Duration</TH><TH>EGM</TH><TH>Zone</TH><TH>Cash in</TH><TH>Credit / win</TH><TH>Net</TH><TH></TH>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionRowsFiltered.length === 0 && (
                      <tr>
                        <td colSpan={11} style={{ padding: 16, color: C.slateL }}>
                          {sessions.length > 0 && txScope === 'since_watchlist' && addedAt
                            ? 'No sessions match “since added to watchlist” — try “All snapshots (window)” or widen the date range in data.'
                            : sessions.length === 0
                              ? 'No session rows returned for this member in the last 84 days. If meter data exists, check imports for gaming_sessions or seed aligned session data.'
                              : 'No sessions match your filters.'}
                        </td>
                      </tr>
                    )}
                    {sessionRowsFiltered.map((s, si) => {
                      const sel = ev.isSelected(`sess-${s.id}`)
                      return (
                        <tr key={s.id ?? `sess-${si}`} style={{ borderBottom: '1px solid #f8f8f8', background: sel ? '#f1f5f9' : '#fff' }}>
                          <td style={{ padding: '10px 8px 10px 14px' }}>
                            <div style={{ cursor: 'pointer' }} onClick={() => ev.toggle({ id: `sess-${s.id}`, type: 'session', label: `Session ${s.date} ${s.timeIn}–${s.timeOut} ${s.egm}` })}>
                              {sel ? <CheckSquare size={15} color="#475569" /> : <Square size={15} color={C.slateL} />}
                            </div>
                          </td>
                          <td style={{ padding: '10px 12px', fontWeight: 700, color: '#1e293b' }}>{s.date}</td>
                          <td style={{ padding: '10px 12px', color: C.green, fontWeight: 800 }}>{s.timeIn}</td>
                          <td style={{ padding: '10px 12px', color: C.red, fontWeight: 800 }}>{s.timeOut}</td>
                          <td style={{ padding: '10px 12px', color: C.slate }}>{s.dur}</td>
                          <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 800 }}>{s.egm}</td>
                          <td style={{ padding: '10px 12px', color: C.slate }}>{s.zone}</td>
                          <td style={{ padding: '10px 12px', color: C.slate }}>{fmt$(s.cashIn)}</td>
                          <td style={{ padding: '10px 12px', fontWeight: 700 }}>{fmt$(s.creditWin)}</td>
                          <td style={{ padding: '10px 12px', fontWeight: 800, color: s.net > 0 ? C.green : C.red }}>{s.net >= 0 ? '+' : ''}{fmt$(s.net)}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <button 
                              type="button"
                              onClick={() => setCctvModalOpen(true) || setCctvModalData({ egm: s.egm, zone: s.zone, arrivalTime: `${s.date} ${s.timeIn}`, departureTime: `${s.date} ${s.timeOut}` })}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', fontSize: T.small, fontWeight: 700, color: C.purple, background: C.purpleBg, border: `1px solid ${C.purpleBd}`, borderRadius: 6, cursor: 'pointer' }}
                            >
                              <Camera size={11} /> Request
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {tab === 'kyc' && (
          <>
            <Card>
              <Head
                title="KYC — Know Your Customer"
                sub={`KYC-${String(memberId).slice(-4)} · ${kycCompleted ? 'Verified' : 'Pending'} · Expires ${profile.date_of_birth ? new Date(new Date(profile.date_of_birth).getTime() + 365 * 86400000).toLocaleDateString('en-AU') : '—'}`}
                action={
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Badge s={kycCompleted ? 'verified' : 'review'} label={kycCompleted ? 'Verified' : 'Pending'} />
                    <button style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: T.small, fontWeight: 700, background: C.slateBg, color: C.slate, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }}>
                      Re-verify
                    </button>
                  </div>
                }
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <div style={{ padding: '16px 20px', borderRight: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Identity Fields</div>
                  {[
                    { label: 'Full Legal Name', value: patronName, verified: true },
                    { label: 'Date of Birth', value: profile.date_of_birth || '-', verified: true },
                    { label: 'Residential Address', value: profile.address || profile.suburb ? [profile.address, profile.suburb, profile.state, profile.postcode].filter(Boolean).join(', ') : '-', verified: false, note: 'Self-declared' },
                    { label: 'Occupation', value: profile.occupation || '-', verified: false, note: 'Self-declared — not independently verified' },
                    { label: 'Phone', value: profile.phone || '-', verified: true },
                    { label: 'Email', value: profile.email || '-', verified: true },
                  ].map((f, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: i < 5 ? '1px solid #f8f8f8' : 'none' }}>
                      {f.verified ? <CheckCircle2 size={14} color={C.green} style={{ marginTop: 2, flexShrink: 0 }} /> : <AlertTriangle size={14} color={C.amber} style={{ marginTop: 2, flexShrink: 0 }} />}
                      <div>
                        <div style={{ fontSize: T.small, color: C.slateL }}>{f.label}</div>
                        <div style={{ fontSize: T.normal, fontWeight: 600, color: '#1e293b' }}>{f.value}</div>
                        {f.note && <div style={{ fontSize: T.small, color: C.amber, marginTop: 1 }}>{f.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Documents Verified</div>
                  <div style={{ background: dvsVerified ? C.greenBg : C.amberBg, border: `1px solid ${dvsVerified ? C.greenBd : C.amberBd}`, borderRadius: 8, padding: '11px 14px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: T.normal, fontWeight: 700, color: '#1e293b' }}>ID Document</span>
                      <Badge s={dvsVerified ? 'verified' : 'review'} label={dvsVerified ? 'Verified' : dvsStatus} />
                    </div>
                    <div style={{ fontSize: T.small, color: C.slate }}>{dvsVerified ? 'Verified via DVS' : `DVS: ${dvsStatus}`}</div>
                  </div>
                  <div style={{ background: dvsVerified ? C.greenBg : C.slateBg, border: `1px solid ${dvsVerified ? C.greenBd : '#e8eef2'}`, borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
                    <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 4 }}>
                      {dvsVerified ? <CheckCircle2 size={13} color={C.green} /> : <AlertTriangle size={13} color={C.amber} />}
                      <span style={{ fontSize: T.small, fontWeight: 800, color: dvsVerified ? C.green : C.amber }}>DVS Gateway — {dvsVerified ? 'Identity Matched' : dvsStatus}</span>
                    </div>
                    <div style={{ fontSize: T.small, color: C.slate }}>DVS Gateway · {dvsVerified ? 'Last verified' : 'Pending'}</div>
                  </div>
                  <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Verification History</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.pri, marginTop: 6, flexShrink: 0 }} />
                    <div><div style={{ fontSize: T.small, color: '#334155' }}>Initial KYC — documents collected</div><div style={{ fontSize: T.small, color: C.slateL }}>System</div></div>
                  </div>
                </div>
              </div>
              <div style={{ padding: '0 20px 16px' }}>
                <Link to={`/profile-safe/search?q=${encodeURIComponent(memberId)}`} style={{ fontSize: T.small, color: '#475569', fontWeight: 600 }}>View full profile →</Link>
              </div>
            </Card>
            <Card>
              <Head
                title="AML / PEP Screening"
                sub={amlChecks[0] ? `SCR-${amlChecks[0].id} · Last screened ${new Date(amlChecks[0].created_at).toLocaleDateString('en-AU')}` : 'No screening on record'}
                action={
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Badge s={(overview.data?.kyc_aml?.aml_derived_category || overview.data?.kyc_aml?.is_pep || amlChecks[0]?.result_count > 0 || alerts.length > 0) ? 'review' : 'clear'} label={overview.data?.kyc_aml?.aml_derived_category || (overview.data?.kyc_aml?.is_pep ? 'PEP' : '') || (amlChecks[0]?.result_count > 0 ? `${amlChecks[0].result_count} for review` : '') || (alerts.length > 0 ? '1 for review' : '') || 'Clear'} />
                    <button onClick={handleReScreen} disabled={amlLoading} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: T.small, fontWeight: 700, background: amlLoading ? '#e2e8f0' : C.slateBg, color: C.slate, border: '1px solid #e2e8f0', borderRadius: 6, cursor: amlLoading ? 'wait' : 'pointer', opacity: amlLoading ? 0.7 : 1 }}>
                      {amlLoading ? 'Screening…' : 'Re-screen'}
                    </button>
                  </div>
                }
              />
              <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 18 }}>
                  {[
                    { key: 'pep', icon: '👤', label: 'PEP Screening', status: kycAml.aml_pep_match ? 'review' : 'clear', detail: kycAml.aml_pep_match ? 'PEP match — domestic or foreign.' : 'No PEP match — domestic or foreign.' },
                    { key: 'sanctions', icon: '🚫', label: 'Sanctions Lists', status: kycAml.aml_sanctions_match ? 'review' : 'clear', detail: kycAml.aml_sanctions_match ? 'Listed on OFAC, UN, Australian Consolidated, or EU sanctions lists.' : 'Not listed on OFAC, UN, Australian Consolidated, or EU sanctions lists.' },
                    { key: 'adverse', icon: '📰', label: 'Adverse Media', status: alerts.length > 0 ? 'review' : 'clear', detail: alerts.length > 0 ? '1 result — review required.' : 'No adverse media matches.' },
                    { key: 'watchlists', icon: '🔎', label: 'Intelligence Watchlists', status: 'clear', detail: 'Not listed on AUSTRAC or AFP intelligence watchlists.' },
                  ].map(cat => {
                    const m = SC[cat.status] || SC.clear
                    return (
                      <div key={cat.key} style={{ border: `1px solid ${m.bd}`, borderTop: `3px solid ${m.color}`, borderRadius: 8, padding: '14px 14px', background: m.bg }}>
                        <div style={{ fontSize: 18, marginBottom: 8 }}>{cat.icon}</div>
                        <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginBottom: 4 }}>
                          {cat.status === 'clear' ? <CheckCircle2 size={13} color={m.color} /> : <AlertTriangle size={13} color={m.color} />}
                          <span style={{ fontSize: T.small, fontWeight: 800, color: m.color }}>{m.label}</span>
                        </div>
                        <div style={{ fontSize: T.small, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>{cat.label}</div>
                        <div style={{ fontSize: T.small, color: C.slate, lineHeight: 1.4 }}>{cat.detail}</div>
                      </div>
                    )
                  })}
                </div>
                {(kycAml.aml_pep_status_changed_at || kycAml.aml_sanctions_status_changed_at || kycAml.aml_first_found_at) && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: T.small, color: C.slate }}>
                    {kycAml.aml_pep_status_changed_at && <span>PEP status changed {new Date(kycAml.aml_pep_status_changed_at).toLocaleString('en-AU')}</span>}
                    {kycAml.aml_pep_status_changed_at && kycAml.aml_sanctions_status_changed_at && ' · '}
                    {kycAml.aml_sanctions_status_changed_at && <span>Sanctions status changed {new Date(kycAml.aml_sanctions_status_changed_at).toLocaleString('en-AU')}</span>}
                    {kycAml.aml_first_found_at && (kycAml.aml_pep_status_changed_at || kycAml.aml_sanctions_status_changed_at) ? ' · ' : ''}
                    {kycAml.aml_first_found_at && <span>First found {new Date(kycAml.aml_first_found_at).toLocaleString('en-AU')}</span>}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div style={{ background: C.slateBg, border: '1px solid #e8eef2', borderRadius: 8, padding: '14px 16px' }}>
                    <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 3 }}>Next re-screen due</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>{new Date(Date.now() + 180 * 86400000).toLocaleDateString('en-AU')}</div>
                    <div style={{ fontSize: T.small, color: C.slateL, marginTop: 2 }}>6-month cycle</div>
                  </div>
                  <div style={{ background: C.slateBg, border: '1px solid #e8eef2', borderRadius: 8, padding: '14px 16px' }}>
                    <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Screening History</div>
                    {amlChecks.length > 0 ? amlChecks.map((c, i) => (
                      <div key={c.id} style={{ display: 'flex', gap: 8, marginBottom: i < amlChecks.length - 1 ? 7 : 0, alignItems: 'flex-start' }}>
                        {c.result_count > 0 ? <AlertTriangle size={13} color={C.amber} style={{ flexShrink: 0, marginTop: 2 }} /> : <CheckCircle2 size={13} color={C.green} style={{ flexShrink: 0, marginTop: 2 }} />}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: T.small, color: '#334155' }}>SCR-{c.id} · {c.result_count} match{c.result_count !== 1 ? 'es' : ''} · {c.reason || 'Screening'}</div>
                          <div style={{ fontSize: T.small, color: C.slateL }}>{c.performed_by || 'System'} · {new Date(c.created_at).toLocaleString('en-AU')}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => viewAmlCheckDetails(c.id)}
                          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: T.small, color: C.priL, background: 'transparent', border: '2px solid transparent', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                        >
                          <Eye size={12} /> View
                        </button>
                      </div>
                    )) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <CheckCircle2 size={13} color={C.green} style={{ flexShrink: 0, marginTop: 2 }} />
                        <div><div style={{ fontSize: T.small, color: '#334155' }}>No screening on record — click Re-screen to run AML/PEP check</div><div style={{ fontSize: T.small, color: C.slateL }}>—</div></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}

        {tab === 'patterns' && (
          <>
            <Card style={{ marginBottom: 16, background: C.amberBg, border: `1px solid ${C.amberBd}` }}>
              <div style={{ padding: '14px 18px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Flag size={15} color={C.amber} style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: T.normal, color: '#78350f', lineHeight: 1.55 }}>
                  <strong>On watchlist because: </strong>
                  {firstAlert?.ref || firstAlert?.alert_type || 'Watchlist alert'}
                  {' — '}
                  {watchlist.count ?? 0} alert{(watchlist.count ?? 0) === 1 ? '' : 's'} on record
                  {watchlist.added_by ? (
                    <span style={{ display: 'block', fontSize: T.small, color: C.slate, marginTop: 4 }}>
                      Added by {watchlist.added_by}
                      {watchlist.first_seen
                        ? ` · ${new Date(watchlist.first_seen).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
                        : ''}
                    </span>
                  ) : null}
                </div>
              </div>
            </Card>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Card style={{ marginBottom: 0 }}>
                <Head title="12-week cash movement trend" sub={addedAt ? "Includes meter cash-out + session credit; ↑ = week added to watchlist" : "Meter snapshots + sessions — not payout tickets"} />
                <div style={{ padding: '16px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 140 }}>
                    {payoutWeeks.map((v, i) => {
                      const weekEnd = now - (11 - i) * weekMs
                      const weekStart = weekEnd - weekMs
                      const isSinceAdded = addedAt != null && addedAt >= weekStart && addedAt < weekEnd
                      const showAddedLabel = isSinceAdded
                      return (
                        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <div style={{ width: '100%', height: `${(v / maxWeek) * 120}px`, borderRadius: '3px 3px 0 0', background: isSinceAdded ? C.red : C.priL, position: 'relative' }}>
                            {showAddedLabel && <div style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)', fontSize: T.small, color: C.red, fontWeight: 800, whiteSpace: 'nowrap' }}>↑ Added</div>}
                          </div>
                          <div style={{ fontSize: T.small, color: C.slateL }}>W{i + 1}</div>
                        </div>
                      )
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 10, fontSize: T.small }}>
                    <span style={{ color: C.priL }}>■ Other weeks</span>
                    <span style={{ color: C.red }}>■ Week added to watchlist</span>
                  </div>
                </div>
              </Card>
              <Card style={{ marginBottom: 0 }}>
                <Head
                  title="Visit Pattern — Day of Week"
                  sub={visitPattern?.has_data
                    ? `${visitPattern.total_visit_days} visit days · 12 months`
                    : `${visitDow.reduce((a, v) => a + v.n, 0)} total visits (current window)`}
                />
                <div style={{ padding: '16px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140 }}>
                    {visitDow.map((v, i) => (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <div
                          style={{ width: '100%', height: `${Math.max(2, (v.n / maxDow) * 110)}px`, borderRadius: '4px 4px 0 0', background: v.n === maxDow && v.n > 0 ? C.amber : C.priL }}
                          title={`${v.d}: ${v.n}`}
                        />
                        <div style={{ fontSize: T.small, color: C.slateL }}>{v.d}</div>
                      </div>
                    ))}
                  </div>
                  {visitPattern?.has_data ? (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '2px 12px', fontSize: T.small, color: C.slate }}>
                      <span>Peak: <strong>{visitPattern.peak_dow}</strong></span>
                      <span>Avg/week: <strong>{visitPattern.avg_visits_per_week}</strong></span>
                      <span>Avg/month: <strong>{visitPattern.avg_visits_per_month}</strong></span>
                      {visitPattern.last_visit_date && (
                        <span>Last visit: <strong>{new Date(visitPattern.last_visit_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: T.small, color: C.slateL, textAlign: 'center', marginTop: 8 }}>Based on current session window only</div>
                  )}
                </div>
              </Card>
            </div>
            <div style={{ height: 16 }} />
            {(() => {
              const apiEgms = gamingBehaviour?.has_data ? (gamingBehaviour.top_egms || []).slice(0, 5) : []
              const topEgms = apiEgms.length > 0
                ? apiEgms.map(e => ({ egm: e.egm_pk || '—', sessions: e.session_count, total: e.total_credit_win ?? 0, pct: e.pct }))
                : (() => {
                    const egmCounts = {}
                    sessionRows.forEach(s => {
                      const egm = s.egm || '-'
                      if (!egmCounts[egm]) egmCounts[egm] = { sessions: 0, total: 0 }
                      egmCounts[egm].sessions += 1
                      egmCounts[egm].total += s.creditWin || 0
                    })
                    const prefs = Object.entries(egmCounts).map(([egm, d]) => ({ egm, sessions: d.sessions, total: d.total, pct: 0 }))
                    const maxS = Math.max(...prefs.map(e => e.sessions), 1)
                    prefs.forEach(e => { e.pct = Math.round((e.sessions / maxS) * 100) })
                    return prefs.sort((a, b) => b.sessions - a.sessions).slice(0, 5)
                  })()
              return (
                <Card>
                  <Head title="EGM Preferences" sub={gamingBehaviour?.has_data ? 'Top 5 · 12 months · for CCTV zone targeting' : 'Most-used machines — for CCTV zone targeting'} />
                  <div style={{ padding: '14px 18px' }}>
                    {topEgms.length === 0 ? (
                      <div style={{ fontSize: T.small, color: C.slateL }}>No session data yet</div>
                    ) : (
                      topEgms.map((e, i) => (
                        <div key={e.egm} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', borderBottom: i < topEgms.length - 1 ? '1px solid #f8f8f8' : 'none' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: T.normal, fontWeight: 800, color: i === 0 ? C.red : '#1e293b', width: 80, flexShrink: 0 }}>{e.egm}</span>
                          <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${e.pct}%`, background: i === 0 ? C.red : i === 1 ? C.amber : C.priL, borderRadius: 4 }} />
                          </div>
                          <span style={{ fontSize: T.small, fontWeight: 700, color: '#1e293b', minWidth: 70, textAlign: 'right' }}>{e.sessions} sessions</span>
                          <button
                            onClick={() => {
                              setCctvModalData({ egm: e.egm, zone: e.zone ?? '-' })
                              setCctvModalOpen(true)
                            }}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 9px', fontSize: T.small, fontWeight: 700, color: C.purple, background: C.purpleBg, border: `1px solid ${C.purpleBd}`, borderRadius: 6, cursor: 'pointer' }}
                          >
                            <Camera size={10} /> CCTV
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              )
            })()}
            <Card>
              <Head title="Gaming Behaviour Profile" sub={gamingBehaviour?.has_data ? '12-month history' : 'current window'} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
                {(() => {
                  const behAvgDur = gamingBehaviour?.avg_duration_min != null
                    ? (gamingBehaviour.avg_duration_min >= 60
                        ? `~${Math.floor(gamingBehaviour.avg_duration_min / 60)}h ${gamingBehaviour.avg_duration_min % 60}m`
                        : `~${gamingBehaviour.avg_duration_min}m`)
                    : avgSessionDuration
                  const behPeak = gamingBehaviour?.peak_hour_label ?? peakArrivalTime
                  const behTotalSessions = gamingBehaviour?.total_sessions ?? sessionRows.length
                  const behAvgCashIn = gamingBehaviour?.avg_cash_in != null ? fmt$(Math.round(gamingBehaviour.avg_cash_in)) : '—'
                  const behPayoutRatio = gamingBehaviour?.payout_ratio != null ? `${Math.round(gamingBehaviour.payout_ratio * 100)}%` : '—'
                  const behMultiSess = gamingBehaviour?.multi_session_days != null ? gamingBehaviour.multi_session_days : '—'
                  return [
                    ['Avg session length', behAvgDur, false],
                    ['Avg cash in/session', behAvgCashIn, false],
                    ['Peak arrival time', behPeak, false],
                    ['Zone preference', sessionRows[0]?.zone || '—', false],
                    ['Total sessions (12 mo)', behTotalSessions, false],
                    ['Multi-session days', behMultiSess, false],
                    ['Payout ratio', behPayoutRatio, false],
                  ].map(([l, v, flag], i, arr) => (
                    <div key={i} style={{ padding: '13px 16px', borderBottom: i < arr.length - 3 ? '1px solid #f8f8f8' : 'none', borderRight: i % 3 !== 2 ? '1px solid #f8f8f8' : 'none' }}>
                      <div style={{ fontSize: T.small, color: C.slateL, marginBottom: 4 }}>{l}</div>
                      <div style={{ fontSize: T.normal, fontWeight: 800, color: flag ? C.red : '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {v} {flag && <AlertTriangle size={12} color={C.amber} />}
                      </div>
                    </div>
                  ))
                })()}
              </div>
            </Card>
          </>
        )}

        {tab === 'log' && (
          <Card>
            <Head
              title="Observation Log"
              sub="All notes since watchlist addition"
              action={
                <button style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', fontSize: T.small, fontWeight: 700, color: C.pri, background: C.priM, border: `1px solid ${C.pri}33`, borderRadius: 6, cursor: 'pointer' }}>
                  <Plus size={11} /> Add Observation
                </button>
              }
            />
            <div style={{ padding: '16px 18px' }}>
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: T.small, fontWeight: 700, color: C.slateL, marginBottom: 6 }}>New Note</div>
                <textarea
                  placeholder="Record a staff observation, patron behaviour note, or update…"
                  value={newNoteText}
                  onChange={e => setNewNoteText(e.target.value)}
                  style={{ width: '100%', minHeight: 80, fontSize: T.normal, border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', color: '#334155' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={addNote} className="px-4 py-2 rounded-md text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800">Save Note</button>
                  <input
                    ref={noteFileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileAttachment}
                    disabled={uploadingAttachment}
                  />
                  <button
                    onClick={() => noteFileInputRef.current?.click()}
                    disabled={uploadingAttachment}
                    className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 disabled:opacity-50"
                  >
                    <Upload size={12} /> {uploadingAttachment ? 'Uploading...' : 'Attach File'}
                  </button>
                </div>
              </div>
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                {memberNotes.length === 0 && <div style={{ fontSize: T.small, color: C.slateL }}>No notes yet</div>}
                {memberNotes.map(n => {
                  const sel = ev.isSelected(`note-${n.id}`)
                  return (
                    <div key={n.id} style={{ display: 'flex', gap: 14, marginBottom: 18, background: sel ? '#f1f5f9' : 'transparent', borderRadius: 8, padding: sel ? '8px 10px' : 0 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Shield size={14} color="#475569" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                          <span style={{ fontSize: T.small, fontWeight: 700, color: '#1e293b' }}>{n.created_by || '—'}</span>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ fontSize: T.small, color: C.slateL }}>{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span>
                            <button onClick={() => ev.toggle({ id: `note-${n.id}`, type: 'note', label: `Observation by ${n.created_by} — ${n.created_at}` })} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', fontSize: 10, fontWeight: 700, background: sel ? '#475569' : C.slateBg, color: sel ? '#fff' : C.slate, border: `1px solid ${sel ? '#475569' : '#e2e8f0'}`, borderRadius: 5, cursor: 'pointer' }}>
                              {sel ? <CheckSquare size={10} /> : <Square size={10} />} Add to Case
                            </button>
                          </div>
                        </div>
                        <div style={{ fontSize: T.normal, color: '#334155', lineHeight: 1.6, background: C.slateBg, border: '1px solid #e8eef2', borderRadius: 7, padding: '10px 14px' }}>
                          {n.note}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </Card>
        )}
      </div>

      <EvidenceBar ev={ev} memberId={memberId} onCreateCase={createCase} />

      {showRemove && (
        <RemoveModal
          patronName={patronName}
          patronId={memberId}
          addedBy={watchlist.added_by || 'System'}
          daysOnWatchlist={watchlist.first_seen ? Math.floor((Date.now() - new Date(watchlist.first_seen).getTime()) / 86400000) : 0}
          onClose={() => setShowRemove(false)}
          onConfirm={handleRemove}
        />
      )}

      {amlModalOpen && amlModalData && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40 }} onClick={() => setAmlModalOpen(false)} />
          <div style={{ position: 'fixed', right: 0, top: 0, height: '100%', width: 'min(1100px, 100%)', background: '#fff', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)', zIndex: 50, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #e8eef2', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                  {(!amlModalData.results || amlModalData.results.length === 0)
                    ? 'Screening clear'
                    : 'AML/PEP match review'}
                </div>
                <div style={{ fontSize: T.small, color: C.slate }}>
                  {amlModalData.name || patronName}
                  {amlModalData.checkId ? ` · Check #${amlModalData.checkId}` : ''}
                  {amlModalData.country ? ` · ${amlModalData.country}` : ''}
                  {Array.isArray(amlModalData.results) && amlModalData.results.length > 0
                    ? ` · ${amlModalData.results.length} result${amlModalData.results.length === 1 ? '' : 's'}`
                    : ''}
                </div>
              </div>
              <button type="button" onClick={() => setAmlModalOpen(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#64748b' }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: 16, overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {(!amlModalData.results || amlModalData.results.length === 0) ? (
                <div style={{ background: C.greenBg, border: `1px solid ${C.greenBd}`, borderRadius: 8, padding: 24, textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><CheckCircle2 size={28} color={C.green} /></div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.green }}>No matches found</div>
                  <div style={{ fontSize: T.small, color: C.slate, marginTop: 6 }}>No PEP · No Sanctions · No Adverse Media</div>
                </div>
              ) : (
                <ProfileAmlMatchReviewPanel
                  results={amlModalData.results}
                  searchName={amlModalData.name || patronName}
                  checkId={amlModalData.checkId || null}
                  initialDecision={amlModalData.analystDecision || null}
                  initialReason={amlModalData.analystReason || ''}
                  saving={amlReviewSaving}
                  onSaveReview={async ({ decision, reason, selected_match }) => {
                    if (!amlModalData.checkId) throw new Error('Check id missing')
                    setAmlReviewSaving(true)
                    try {
                      const resp = await authFetch(
                        getLocalApiUrl(`/api/aml/checks/${encodeURIComponent(String(amlModalData.checkId))}/review`),
                        {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ decision, reason, selected_match }),
                        }
                      )
                      const data = await resp.json().catch(() => ({}))
                      if (!resp.ok || !data?.success) throw new Error(data?.error || 'Failed to save review')
                      setAmlModalData((prev) => prev ? { ...prev, analystDecision: decision, analystReason: reason || '' } : prev)
                      await loadAmlChecks()
                      await loadOverview()
                    } finally {
                      setAmlReviewSaving(false)
                    }
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}

      {cctvModalOpen && cctvModalData && (
        <CctvModal
          egm={cctvModalData.egm}
          zone={cctvModalData.zone}
          date={cctvModalData.date}
          time={cctvModalData.time}
          arrivalTime={cctvModalData.arrivalTime}
          departureTime={cctvModalData.departureTime}
          onClose={() => setCctvModalOpen(false)}
        />
      )}
    </div>
  )
}
